import { useCallback, useEffect, useRef, useState } from 'react';
import { findAgent, scanAgentApi, type ScanAgentApi } from './scan-agent';

export interface FolderAgentState {
  /** Looking for Paige Scan, found it, or nothing answered on this computer. */
  phase: 'checking' | 'ready' | 'missing';
  /** Paige Scan brought its Watched folders window forward for this job. */
  opened: boolean;
  busy: boolean;
  problem: string | null;
}

/**
 * Paige Scan on this computer, for setting up a watched folder: whether it answers, and the call that
 * brings its Watched folders window forward with the job already picked. The folder itself is chosen
 * there, on the computer that can see it; the page never names a path.
 */
export function useFolderAgent(
  active: boolean,
): [FolderAgentState, (job: { id: string; name: string }) => void] {
  const [state, setState] = useState<FolderAgentState>({
    phase: 'checking',
    opened: false,
    busy: false,
    problem: null,
  });
  const api = useRef<ScanAgentApi | null>(null);
  useEffect(() => {
    if (!active) return;
    let live = true;
    void findAgent().then((found) => {
      if (!live) return;
      api.current = found.agent ? scanAgentApi(found.agent) : null;
      setState((prev) => ({ ...prev, phase: found.agent ? 'ready' : 'missing' }));
    });
    return () => {
      live = false;
    };
  }, [active]);
  const open = useCallback((job: { id: string; name: string }) => {
    const agent = api.current;
    if (!agent) return;
    setState((prev) => ({ ...prev, busy: true, problem: null }));
    agent
      .openFolders({ jobId: job.id, jobName: job.name })
      .then(() => setState((prev) => ({ ...prev, busy: false, opened: true })))
      .catch((error: unknown) =>
        setState((prev) => ({
          ...prev,
          busy: false,
          problem: error instanceof Error ? error.message : 'Paige Scan did not answer.',
        })),
      );
  }, []);
  return [state, open];
}
